<script setup lang="ts">
  import { defineProps, type PropType } from 'vue'
  import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
  import { type RuleExpression, useField } from 'vee-validate'

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: String,
      required: false
    },
    rows: {
      type: String,
      required: false,
      default: '3'
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  })

  const { value, errorMessage, handleBlur, handleChange, meta, errors } = useField(
    props.name,
    props.rules,
    {
      syncVModel: true
    }
  )
</script>

<template>
  <div>
    <label
      v-if="label"
      class="mb-2 block text-sm font-medium"
    >
      {{ label }}
    </label>
    <div :class="errorMessage ? 'relative' : ''">
      <textarea
        v-model="value"
        class="block w-full rounded-lg px-3 py-2 text-sm text-gray-800 placeholder:text-gray-500 focus:z-10"
        :name="props.name"
        :class="
          errorMessage
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-200 focus:border-purple-600 focus:ring-purple-600'
        "
        :rows="rows"
        :placeholder="placeholder"
        :disabled="props.disabled"
        @input="handleChange"
        @change="handleChange"
        @blur="handleBlur"
      ></textarea>
      <div
        v-if="errorMessage"
        class="pointer-events-none absolute end-0 top-0 flex items-center p-3"
      >
        <ExclamationCircleIcon class="size-4 flex-shrink-0 text-red-500" />
      </div>
    </div>
    <p
      v-if="errorMessage"
      class="mt-1 text-xs text-red-600"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<style scoped>
  textarea:disabled {
    background-color: #f9f9f9; /* Light grey background */
    color: #aaa; /* Gray text */
    cursor: not-allowed; /* Not allowed cursor */
  }
</style>
